/* ===== game/fluid-fx.ts — 유체의 그림 · 손맛: 흐린 물 · 김 · 물에 떨어지는 것의 첨벙임과 물결 ===== */
import { TAU } from '../../engine/core/math.js';
import { mixin } from '../../engine/core/mixin.js';
import { T } from '../data.js';
import { FLUID_KIND } from '../data/materials.js';
import { TS } from '../world.js';
import { Part } from '../entity.js';
import { Game } from '../game.js';
/* 물은 양을 보존한다(world/fluid.ts) — 여기는 보이는 쪽만. 떨어지는 것이 수면을 치면 크기 · 빠르기만큼 물기둥 · 물방울 · 수면 물결이 인다
   (몸집 큰 보스가 뛰어들면 크게). 물결은 고리가 아니라 수면을 따라 양옆으로 퍼지는 짧은 빛 마루다. 사연: docs/code-history.md#h264 */

type C2 = CanvasRenderingContext2D;
const SPLASH_COL: Record<number, [string, string]> = { 1: ['#dff2ff', '#9fd0f0'], 2: ['#d8f0ff', '#8fc0e0'], 3: ['#ffd27a', '#ff8a3a'], 4: ['#b8a888', '#6a5a44'] };

export const FluidFx: Bag = {
  /** 물에 무엇이 떨어졌다 — x · 수면 y · 아래로 빠르기 · 몸 폭 · 액체 종류 */
  fluidSplash(x: number, y: number, vy: number, w: number, K: number) {
    const s = Math.min(1.8, Math.max(0.3, (vy / 500) * (w / 24)));
    const [c1, c2] = SPLASH_COL[K] || SPLASH_COL[1];
    const n = Math.round(8 + s * 18);
    for (let i = 0; i < n; i++) {                                         // 왕관 — 가장자리에서 비스듬히 솟는 물방울
      const side = i % 2 ? 1 : -1, a = -Math.PI / 2 + side * (0.25 + Math.random() * 0.6);
      const sp = (140 + Math.random() * 220) * (0.6 + s * 0.5);
      const p = new Part(x + side * w * 0.3 * Math.random(), y - 2, Math.random() < 0.6 ? c1 : c2, 0, 0.5 + Math.random() * 0.4, { spd: 0, g: 1, r: 1 + Math.random() * 1.6 * s, drag: 0.99 });
      p.vx = Math.cos(a) * sp; p.vy = Math.sin(a) * sp; this.parts.push(p);
    }
    if (s > 0.6) for (let i = 0; i < 6; i++) {                           // 큰 것 — 가운데로 되솟는 물기둥
      const p = new Part(x + (Math.random() - 0.5) * 6, y - 2, c1, 0, 0.7, { spd: 0, g: 1, r: 1.6 + s, drag: 0.99 });
      p.vx = (Math.random() - 0.5) * 30; p.vy = -(260 + Math.random() * 160) * s; this.parts.push(p);
    }
    const R = this.ripples || (this.ripples = []);
    if (R.length < 24) R.push({ x, y, t: 0, life: 1.2 + s * 0.6, s, K });
    if (K !== 3) this.sfxAt ? this.sfxAt('splash', x / TS, y / TS, 1.1 - s * 0.25) : this.sfx && this.sfx('splash');
    else this.sfxAt && this.sfxAt('mat_ember', x / TS, y / TS, 0.7);
  },
  /** 몸이 액체에 막 들어섰다(Ent.move) — 수면을 찾아 첨벙 */
  entSplash(e: any, prev: number) {
    if (prev > 0.05 || !(e.submerged > 0) || (e.vy || 0) < 140) return;
    const w = this.world, { WW } = w.dims, tx = Math.floor(e.cx / TS);
    let ty = Math.floor((e.y + e.h - 2) / TS);
    const K = FLUID_KIND[w.get(tx, ty)]; if (!K) return;
    while (ty > 1 && FLUID_KIND[w.get(tx, ty - 1)] === K) ty--;
    const k = ty * WW + tx, top = (ty + 1) * TS - w.amtAt(k) / 255 * TS;
    this.fluidSplash(e.cx, top, e.vy, e.w, K);
  },
  /** 걸음마다 — 용암이 굳은 자리의 김 */
  fluidFx(dt: number) {
    const w = this.world, q = w && w.fxQ; if (!q) { if (w) w.fxQ = []; return; }
    let hiss = 0;
    while (q.length) {
      const [x, y] = q.pop()!;
      for (let i = 0; i < 4; i++) this.parts.push(new Part((x + Math.random()) * TS, (y + 0.3) * TS, 'rgba(230,236,240,.65)', -40, 1.3, { g: -0.2, r: 2.2, spd: 0.3, drag: 0.92 }));
      if (!hiss++ && Math.abs(x * TS - this.player.cx) < 700) this.sfxAt && this.sfxAt('mat_ember', x, y, 0.55);
    }
    const R = this.ripples; if (R) for (let i = R.length - 1; i >= 0; i--) { R[i].t += dt; if (R[i].t > R[i].life) R.splice(i, 1); }
  },
  /** 흐린 물 — 잿물이 녹아든 물칸에 탁한 갈색을 덧칠한다(tiles 단계) */
  rMurk(f: any) {
    const w = this.world; if (!w || !w.murkSet || !w.murkSet.size) return;
    const c: C2 = f.c, { WW } = w.dims;
    for (const k of w.murkSet) {
      const tx = k % WW, ty = (k / WW) | 0;
      if (tx < f.tx0 - 1 || tx > f.tx1 + 1 || ty < f.ty0 - 1 || ty > f.ty1 + 1) continue;
      const a = w.murk[k] / 255, top = (ty + 1) * TS - w.amtAt(k) / 255 * TS;
      c.fillStyle = `rgba(74,60,42,${0.55 * a})`; c.fillRect(tx * TS - f.camX, top - f.camY, TS, (ty + 1) * TS - top);
    }
  },
  /** 수면 물결 — 떨어진 자리에서 수면을 따라 양옆으로 번지는 빛 마루 둘 · 가운데 거품(drops 단계) */
  rRipples(f: any) {
    const R = this.ripples; if (!R || !R.length) return;
    const c: C2 = f.c;
    for (const r of R) {
      const u = r.t / r.life, x = r.x - f.camX, y = r.y - f.camY, d = 6 + u * 70 * r.s, a = (1 - u) * 0.8;
      c.strokeStyle = r.K === 3 ? `rgba(255,220,140,${a})` : r.K === 4 ? `rgba(200,184,150,${a})` : `rgba(235,250,255,${a})`;
      c.lineWidth = 1.6 * (1 - u) + 0.6;
      for (const s of [-1, 1]) for (let j = 0; j < 2; j++) {
        const dd = d * (1 - j * 0.35);
        c.beginPath(); c.moveTo(x + s * dd, y - 1 - (1 - u) * 2); c.quadraticCurveTo(x + s * (dd + 6), y - 3 * (1 - u), x + s * (dd + 12), y); c.stroke();
      }
      if (u < 0.5) { c.fillStyle = `rgba(255,255,255,${0.6 * (1 - u * 2)})`; for (let i = 0; i < 5; i++) { c.beginPath(); c.arc(x + (i - 2) * 4 * r.s, y - 1, 1.5 + r.s, 0, TAU); c.fill(); } }
    }
  },
};
void T;
mixin(Game.prototype, FluidFx, true);
